import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { RCta } from '../RCta';
import type { WregScreenEvent } from './wregScreenEvents';
import { INTAKE_CONTACT_FIELDS, useIntakeErrors, type IntakeErrorKey } from './wregIntakeErrors';
import { WregIntakeShell } from './WregIntakeShell';
import { MailIcon, MaskedRow, PhoneIcon, TextRow } from './WregIntakeRows';
import {
  EMPTY_INTAKE,
  phoneMaskOptions,
  formatPhone,
  intakeContactIsComplete,
  type WregIntakeForm,
  type WregIntakeTextField,
} from './wregIntakeFields';

type Props = {
  notice?: string | null;
  initial?: WregIntakeForm;
  onContinue: (patch: Partial<WregIntakeForm>) => void;
  onEvent?: WregScreenEvent;
};

const WregIntakeContact = ({ notice, initial, onContinue, onEvent }: Props) => {
  const { t } = useTranslation();
  const [f, setF] = useState<WregIntakeForm>(initial ?? EMPTY_INTAKE);

  const valid = intakeContactIsComplete(f);
  const { errors, focus, blur, showError, errorId, validateBeforeSubmit } = useIntakeErrors({
    fields: INTAKE_CONTACT_FIELDS,
    form: f,
    valid,
    step: 'intake-contact',
    t,
    onEvent,
  });

  const set = (k: WregIntakeTextField) => (e: ChangeEvent<HTMLInputElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value }));

  const row = (field: IntakeErrorKey) => ({
    invalid: showError(field),
    errorId: errorId(field),
    error: showError(field) ? errors[field] : undefined,
    onFocus: focus(field),
    onBlur: blur(field),
  });

  return (
    <WregIntakeShell
      step="intake-contact"
      title={t('wreg.intake.contact.title')}
      notice={notice}
      onEvent={onEvent}
      footer={
        <>
          <RCta
            beforeClick={validateBeforeSubmit}
            dimmed={!valid}
            onClick={() => onContinue({ phone: f.phone, email: f.email })}
          >
            {t('wreg.intake.next')}
          </RCta>
        </>
      }
    >
      <MaskedRow
        id="wreg-int-phone"
        label={t('wreg.intake.phone.label')}
        autoComplete="tel-national"
        type="tel"
        inputMode="numeric"
        lead={<PhoneIcon />}
        hint={t('wreg.intake.phone.hint')}
        options={phoneMaskOptions}
        unmask
        value={f.phone}
        display={formatPhone(f.phone)}
        mask={t('wreg.intake.phone.ph')}
        onAccept={(phone) => setF((p) => ({ ...p, phone }))}
        {...row('phone')}
      />
      <TextRow
        id="wreg-int-email"
        label={t('wreg.intake.email.label')}
        autoComplete="email"
        type="email"
        lead={<MailIcon />}
        placeholder={t('wreg.intake.email.ph')}
        hint={t('wreg.intake.email.hint')}
        value={f.email}
        onChange={set('email')}
        {...row('email')}
      />
    </WregIntakeShell>
  );
};

export { WregIntakeContact };
